import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DeleteMatterDialog } from './delete-matter-dialog';
import { Button } from '../../../components/shadcn/button';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteMatter } from '@/api/mutations/useDeleteMatter';
import { Matter, Case } from '@/api/types/backend-dto-types';
import isPlural from '@/helpers/isPlural';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

interface MatterCardProps {
  matter: Matter;
  matchedCases?: Array<Pick<Case, 'id' | 'name'>>;
  searchFilter?: string;
}

export function MatterCard({ matter, matchedCases = [], searchFilter }: MatterCardProps) {
  const navigate = useNavigate();

  const { isLoading: isDeletingMatter } = useDeleteMatter();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { i18n } = useTranslation();

  const hasMatches = matchedCases.length > 0;
  const matterLink = `/matters/${matter.id}`;

  // Delete permission is resolved per-matter on the backend (matter-level overrides and
  // seat-based creator escalation applied); the URL-scoped useHasPermission hook cannot
  // resolve overrides on the list page where there is no matterId in the route.
  const canDelete = matter.canDelete;

  return (
    <div
      onClick={() => {
        trackCustomEvent(TrackingEventsV2.NavigationClickMatterCard, {
          matterId: matter.id,
        });
        navigate(matterLink);
      }}
      className={cn(
        'group cursor-pointer rounded-lg border bg-gray-100 duration-150 hover:border-gray-300',
        isDeletingMatter ? 'opacity-50' : 'opacity-100',
      )}
    >
      <div className="rounded-lg border-b bg-white px-5 py-3.5 pr-3">
        <div className="flex justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="truncate text-base font-medium text-gray-800">{matter.name}</h3>

              {searchFilter && hasMatches && (
                <Chip className="rounded-full border border-dashed border-sky-300 bg-sky-50 text-sky-500">
                  <Icon className="lucide--search" />
                  {matchedCases.length} matching {matchedCases.length === 1 ? 'Subset' : 'Subsets'}
                </Chip>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                onClick={(e) => e.preventDefault()}
                disabled={isDeletingMatter}
                data-testid="matter-card-ellipsis-menu"
              >
                <Icon className="lucide--ellipsis-vertical" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canDelete ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={isDeletingMatter}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <Icon className="lucide--trash-2" />
                  Delete matter
                </DropdownMenuItem>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      disabled
                      className="cursor-not-allowed text-gray-400"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Icon className="lucide--trash-2 h-4 w-4" />
                      Delete Matter
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xl" sideOffset={16} avoidCollisions={false}>
                    <p>You don't have permission to delete this Matter. Ask a Matter admin or an org administrator.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex h-14 flex-col justify-end">
          {matter.code && <p className="truncate font-mono text-gray-400">{matter.code}</p>}

          <div className="flex grow items-end gap-3 text-gray-600">
            <p className="flex items-center gap-1.5">
              <Icon className="lucide--group text-sky-500" />
              <span className="font-medium">
                {matter.casesCount ?? 0}{' '}
                <span className="font-normal">
                  {isPlural({ one: 'subset', more: 'subsets', number: matter.casesCount ?? 0 })}
                </span>
              </span>
            </p>

            <p className="flex items-center gap-1.5">
              <Icon className="lucide--file-text text-gray-400" />
              <span className="font-medium">
                {matter.docsCount ?? 0}{' '}
                <span className="font-normal">
                  {isPlural({ one: 'document', more: 'documents', number: matter.docsCount ?? 0 })}
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="truncate px-4.5 py-2.5 text-xs text-gray-400">
        Created{' '}
        {Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeStyle: 'short' }).format(
          new Date(matter.createdDate),
        )}
        {matter.creatorEmail && ` by ${matter.creatorEmail}`}
      </div>

      <DeleteMatterDialog
        matterId={matter.id}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </div>
  );
}
